import { useEffect, useRef, useState } from 'react';
import { fetchUserSettings, saveUserSetting } from '../lib/api/settings';
import type { AppUser } from '../types/api';
import type { ChartLayout, JsonValue, UserSettings } from '../types/settings';
import { chartLayoutStorageKey, chartSettingsStorageKey, cloudSyncSettingKey, remoteLayoutKey, remoteSettingsKey } from '../types/settings';
import type { SettingsAdapter } from '../types/charting-library';
import { loadStoredJSON, storeJSON } from './storage';

interface ChartPersistenceState {
  ready: boolean;
  layout: ChartLayout | null;
  settings: UserSettings;
  status: string;
  error: string;
  cloudSyncEnabled: boolean;
  settingsAdapter: SettingsAdapter;
  setCloudSyncEnabled(enabled: boolean): void;
  syncNow(layout: ChartLayout): Promise<void>;
  persistLayout(layout: ChartLayout): void;
  flushSettings(): void;
}

export function useChartPersistence(user: AppUser | null): ChartPersistenceState {
  const [ready, setReady] = useState(false);
  const [layout, setLayout] = useState<ChartLayout | null>(null);
  const [settings, setSettings] = useState<UserSettings>({});
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [cloudSyncEnabled, setCloudSyncEnabledState] = useState(() => loadCloudSyncEnabledFromSettings());
  const settingsRef = useRef<UserSettings>({});
  const timerRef = useRef<number | null>(null);
  const userRef = useRef(user);
  const cloudSyncEnabledRef = useRef(cloudSyncEnabled);
  const migratedChartSetupRef = useRef<Set<string>>(new Set());

  userRef.current = user;
  cloudSyncEnabledRef.current = cloudSyncEnabled;

  useEffect(() => {
    let cancelled = false;
    setReady(false);
    const localLayout = loadStoredJSON<ChartLayout | null>(chartLayoutStorageKey, null);
    const localSettings = sanitizeUserSettings(loadStoredJSON<UserSettings>(chartSettingsStorageKey, {}));

    async function load() {
      if (!user) {
        if (cancelled) return;
        settingsRef.current = localSettings;
        setLayout(localLayout);
        setSettings(localSettings);
        setStatus('');
        setError('');
        setReady(true);
        return;
      }

      try {
        const remoteSettings = await fetchUserSettings();
        if (cancelled) return;
        const remoteChartSettings = isUserSettings(remoteSettings[remoteSettingsKey])
          ? sanitizeUserSettings(remoteSettings[remoteSettingsKey])
          : null;
        const remoteLayout = isChartLayout(remoteSettings[remoteLayoutKey]) ? remoteSettings[remoteLayoutKey] : null;
        const nextLayout = remoteLayout ?? localLayout;
        const nextSettings = mergeCloudSyncSetting(remoteChartSettings, localSettings);
        const effectiveCloudSync = getEffectiveCloudSync(nextSettings);
        const migrationSaves: Promise<void>[] = [];
        const layoutMigrationKey = `${user.id}:${remoteLayoutKey}`;
        const settingsMigrationKey = `${user.id}:${remoteSettingsKey}`;

        if (!remoteLayout && localLayout && !migratedChartSetupRef.current.has(layoutMigrationKey)) {
          migrationSaves.push(saveUserSetting(remoteLayoutKey, localLayout).then(() => {
            migratedChartSetupRef.current.add(layoutMigrationKey);
          }));
        }
        if (!remoteChartSettings && Object.keys(localSettings).length > 0 && !migratedChartSetupRef.current.has(settingsMigrationKey)) {
          migrationSaves.push(saveUserSetting(remoteSettingsKey, localSettings).then(() => {
            migratedChartSetupRef.current.add(settingsMigrationKey);
          }));
        }

        settingsRef.current = nextSettings;
        setLayout(nextLayout);
        setSettings(nextSettings);
        setCloudSyncEnabledState(effectiveCloudSync);
        if (nextLayout) {
          storeJSON(chartLayoutStorageKey, nextLayout);
        }
        storeJSON(chartSettingsStorageKey, nextSettings);
        setStatus(effectiveCloudSync ? 'Cloud sync enabled' : 'Cloud sync disabled');
        if (migrationSaves.length > 0) {
          void Promise.all(migrationSaves)
            .then(() => {
              setStatus('Local charts copied to cloud');
              setError('');
            })
            .catch((err) => {
              console.error('migrateChartSetup:', err);
              setError(err instanceof Error ? `Chart migration failed: ${err.message}` : 'Chart migration failed');
            });
        }
      } catch (err) {
        if (cancelled) return;
        settingsRef.current = localSettings;
        setLayout(localLayout);
        setSettings(localSettings);
        setError(err instanceof Error ? `Cloud sync unavailable: ${err.message}` : 'Cloud sync unavailable');
      } finally {
        if (!cancelled) setReady(true);
      }
    }

    void load();

    return () => {
      cancelled = true;
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
  }, [user]);

  function persistRemote(remoteKey: string, localKey: string, value: JsonValue) {
    const persistedValue = remoteKey === remoteSettingsKey && isUserSettings(value)
      ? sanitizeUserSettings(value)
      : value;

    storeJSON(localKey, persistedValue);
    if (!userRef.current || !cloudSyncEnabledRef.current) return;

    saveUserSetting(remoteKey, persistedValue)
      .then(() => {
        setStatus('Synced to cloud');
        setError('');
      })
      .catch((err) => {
        console.error(`saveUserSetting(${remoteKey}):`, err);
        setError(err instanceof Error ? `Sync failed: ${err.message}` : 'Sync failed');
      });
  }

  function scheduleSettingsPersist() {
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      persistRemote(remoteSettingsKey, chartSettingsStorageKey, settingsRef.current);
    }, 400);
  }

  function flushSettings() {
    if (!timerRef.current) return;
    window.clearTimeout(timerRef.current);
    timerRef.current = null;
    persistRemote(remoteSettingsKey, chartSettingsStorageKey, settingsRef.current);
  }

  function persistLayout(nextLayout: ChartLayout) {
    setLayout(nextLayout);
    persistRemote(remoteLayoutKey, chartLayoutStorageKey, nextLayout);
  }

  async function syncNow(nextLayout: ChartLayout) {
    const nextSettings = sanitizeUserSettings({
      ...settingsRef.current,
      [cloudSyncSettingKey]: cloudSyncEnabled,
    });

    setLayout(nextLayout);
    setSettings(nextSettings);
    settingsRef.current = nextSettings;
    storeJSON(chartLayoutStorageKey, nextLayout);
    storeJSON(chartSettingsStorageKey, nextSettings);

    if (!user) {
      setStatus('Saved locally');
      return;
    }

    try {
      await Promise.all([
        saveUserSetting(remoteLayoutKey, nextLayout),
        saveUserSetting(remoteSettingsKey, nextSettings),
      ]);
      setStatus('Synced to cloud');
      setError('');
    } catch (err) {
      setError(err instanceof Error ? `Sync failed: ${err.message}` : 'Sync failed');
      throw err;
    }
  }

  function setCloudSyncEnabled(enabled: boolean) {
    const nextSettings = { ...settingsRef.current, [cloudSyncSettingKey]: enabled };
    settingsRef.current = nextSettings;
    setSettings(nextSettings);
    storeJSON(chartSettingsStorageKey, nextSettings);
    setCloudSyncEnabledState(enabled);
    setStatus(enabled ? 'Cloud sync enabled' : 'Cloud sync disabled');
    setError('');

    if (user) {
      saveUserSetting(remoteSettingsKey, nextSettings).catch((err) => {
        console.error(`saveUserSetting(${remoteSettingsKey}):`, err);
        setError(err instanceof Error ? `Sync failed: ${err.message}` : 'Sync failed');
      });
    }
  }

  const settingsAdapterRef = useRef<SettingsAdapter | null>(null);
  if (!settingsAdapterRef.current) settingsAdapterRef.current = {
    initialSettings: settings,
    setValue(key: string, value: JsonValue) {
      if (isUnsafeChartSettingKey(key)) return;
      if (JSON.stringify(settingsRef.current[key]) === JSON.stringify(value)) return;
      settingsRef.current = { ...settingsRef.current, [key]: value };
      setSettings(settingsRef.current);
      scheduleSettingsPersist();
    },
    removeValue(key: string) {
      if (!(key in settingsRef.current)) return;
      const next = { ...settingsRef.current };
      delete next[key];
      settingsRef.current = next;
      setSettings(next);
      scheduleSettingsPersist();
    },
  };
  settingsAdapterRef.current.initialSettings = settings;

  return {
    ready,
    layout,
    settings,
    status,
    error,
    cloudSyncEnabled,
    settingsAdapter: settingsAdapterRef.current,
    setCloudSyncEnabled,
    syncNow,
    persistLayout,
    flushSettings,
  };
}

function loadCloudSyncEnabledFromSettings(): boolean {
  const settings = loadStoredJSON<UserSettings>(chartSettingsStorageKey, {});
  return settings[cloudSyncSettingKey] !== false;
}

function getEffectiveCloudSync(settings: UserSettings): boolean {
  return settings[cloudSyncSettingKey] !== false;
}

function mergeCloudSyncSetting(remoteSettings: UserSettings | null, localSettings: UserSettings): UserSettings {
  if (!remoteSettings) return localSettings;
  if (typeof remoteSettings[cloudSyncSettingKey] === 'boolean') return remoteSettings;

  return {
    ...remoteSettings,
    [cloudSyncSettingKey]: localSettings[cloudSyncSettingKey] === false ? false : true,
  };
}

function isChartLayout(value: JsonValue | undefined): value is ChartLayout {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function isUserSettings(value: JsonValue | undefined): value is UserSettings {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function sanitizeUserSettings(settings: UserSettings): UserSettings {
  return Object.fromEntries(
    Object.entries(settings).filter(([key]) => !isUnsafeChartSettingKey(key)),
  );
}

function isUnsafeChartSettingKey(key: string): boolean {
  return key === 'chartproperties'
    || key === 'chartproperties.mainSeriesProperties'
    || key === 'chart.lastUsedTimeBasedResolution';
}
